<template>
  <Modal @hide="reset">
    <template #activator="{ show }">
      <PlanLockGuard>
        <button
          class="flex hover:bg-blue-50 items-center w-full py-[10px] pl-7 pr-2.5 gap-2 text-slate-700 text-sm font-medium"
          :disabled="!isPlanEditable"
          @click="show"
        >
          <ul class="space-y-2 w-full">
            <li
              v-for="(provider, index) in sourcesList"
              :key="index"
              class="w-full flex items-center justify-between"
            >
              <div class="flex gap-3">
                <img
                  :src="provider.iconUrl"
                  class="rounded-full w-5 h-5 object-cover object-center"
                  alt="Country Flag"
                />
                <span
                  class="text-[13px] line-clamp-1 font-medium text-slate-700"
                >
                  {{ provider.name }}
                </span>
              </div>
              <span
                v-if="provider.isEvidenceMetadataExtractionTechnology"
                v-tooltip.right="'Evidence metadata extraction technology'"
              >
                <EvidenceLogo class="w-4 h-4 text-primary-500" />
              </span>
            </li>
          </ul>
        </button>
      </PlanLockGuard>
    </template>

    <template #header="{ hide }">
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-2 text-blue-800 items-center">
          <EvidenceLogo class="w-7" />
          <p class="font-semibold">Incidents Database</p>
        </div>
        <button class="p-2" @click="hide">
          <XMarkIcon class="w-6 text-slate-400 hover:text-slate-600" />
        </button>
      </div>
    </template>

    <template #content>
      <div
        class="w-[980px] create-review-modal flex flex-col rounded-2xl relative"
      >
        <div
          v-if="isSearching"
          class="overflow-hidden absolute top-0 left-0 w-full h-1"
        >
          <div class="line absolute bg-primary-500/20 h-1"></div>
          <div class="absolute bg-primary-500 h-1 inc"></div>
          <div class="absolute bg-primary-500 h-1 dec"></div>
        </div>

        <div class="px-6 mt-2 space-y-2">
          <div class="flex gap-2 items-end">
            <TextInput
              v-model="device"
              label="Device"
              placeholder="Enter device name"
              class="flex-1"
              :input-classes="'px-3 py-1'"
              :disabled="isSearching"
              @enter="onSearch"
            />
            <TextInput
              v-model="manufacturer"
              label="Manufacturer"
              placeholder="Enter manufacturer name"
              class="flex-1"
              :input-classes="'px-3 py-1'"
              :disabled="isSearching"
              @enter="onSearch"
            />
          </div>
          <div class="flex gap-2 items-end">
            <DatePicker
              v-model="startDate"
              placeholder="Start Date"
              class="flex-1"
            />
            <DatePicker
              v-model="endDate"
              placeholder="End Date"
              class="flex-1"
            />
          </div>
          <div class="mt-6 flex flex-col gap-3">
            <template v-for="source in sourcesList" :key="source.id">
              <div
                class="flex items-center justify-between border p-3 rounded-md transition-colors duration-100 w-full text-left"
                :class="[
                  enabledSources.includes(source.id)
                    ? 'bg-blue-100 border-blue-300'
                    : 'bg-gray-50/50 hover:bg-gray-100',
                ]"
              >
                <div class="flex items-center gap-2 w-40">
                  <input
                    type="checkbox"
                    class="rounded border-gray-300 text-blue-600"
                    :checked="enabledSources.includes(source.id)"
                    :disabled="isSearching"
                    @change.stop="toggleSource(source.id)"
                  />
                  <img
                    :src="source.iconUrl"
                    class="rounded-full w-6 h-6 object-cover object-center"
                    alt="Country Flag"
                  />
                  <span class="font-semibold">{{ source.name }}</span>
                </div>
                <span
                  v-if="
                    sourcesState[source.id].query &&
                    enabledSources.includes(source.id)
                  "
                  class="flex-1 line-clamp-1 truncate"
                  :title="sourcesState[source.id].query!"
                >
                  {{ sourcesState[source.id].query }}
                </span>
                <div class="flex items-center gap-4 w-full justify-end">
                  <span
                    v-if="sourcesState[source.id].loading"
                    class="text-blue-600"
                  >
                    Searching<span class="dot-anim" />
                  </span>
                  <span
                    v-else-if="sourcesState[source.id].error"
                    class="text-red-600"
                  >
                    {{ sourcesState[source.id].error }}
                    <button
                      class="ml-2 px-2 py-1 bg-red-100 rounded"
                      @click.stop="retrySource(source.id)"
                    >
                      Retry
                    </button>
                  </span>
                  <span
                    v-else-if="
                      sourcesState[source.id].query &&
                      enabledSources.includes(source.id)
                    "
                  >
                    Found {{ sourcesState[source.id].items.length }} items
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ hide }">
      <div class="p-6 flex justify-between items-center w-full">
        <div></div>
        <div>
          <button
            class="truncate flex flex-row gap-2 bg-blue-800 text-white items-center py-2 px-4 rounded-md text-base font-medium"
            :class="{ '!bg-slate-100 !text-black/50': !canImport }"
            :disabled="isSearching || !canImport"
            @click="
              () => {
                importIncidents().then(() => hide())
              }
            "
          >
            <ImportIcon
              v-if="!isSearching"
              class="w-5 h-5 text-white"
              :class="{ '!text-black/50': !canImport }"
            />
            <span v-if="isSearching">
              <Spinner color="white" />
            </span>
            <span v-else>
              {{ `Import ${incidentsCount} incidents` }}
            </span>
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import TextInput from '@app/components/Global/Inputs/TextInput.vue'
import Modal from '@app/components/Global/Modal/Modal.vue'
import PlanLockGuard from '@app/views/Review/Plan/PlanLockGuard.vue'
import Spinner from '@app/components/Global/Spinner.vue'
import ImportIcon from '@app/components/Icons/ImportIcon.vue'
import EvidenceLogo from '@app/components/Icons/EvidenceLogo.vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { DaenApiClient } from '@infrastructure/daenApi/daenApi.client'
import { MaudeApiClient } from '@infrastructure/maudeApi/maudeApi.client'
import { MdidApiClient } from '@infrastructure/mdidApi/mdidApi.client'
import { OpenFdaApiClient } from '@infrastructure/openFdaApi/openFdaApi.client'
import { ref, computed, watch } from 'vue'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import useLoading from '@app/composables/use-loading'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import useBuiltInImportSources from '@app/composables/use-built-in-import-sources'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { Review } from '@core/domain/models/review.model'
import { IncidentMetadata } from '@core/domain/models/reviewItemMetadata/incidentMetadata'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import DatePicker from '@app/components/Global/Inputs/DatePicker.vue'

defineProps<{
  isPlanEditable: boolean
}>()
const sourcesList: {
  id: string
  name: string
  service: { search: (query: string) => Promise<IncidentMetadata[]> }
  iconUrl: string
  isEvidenceMetadataExtractionTechnology?: boolean
}[] = [
  {
    id: BuiltInImportSourceId.OPEN_FDA,
    name: 'OpenFDA',
    service: {
      search: (query: string) =>
        new OpenFdaApiClient().search({
          query,
          dateFilter: {
            startDate: startDate.value,
            endDate: endDate.value,
          },
        }),
    },
    iconUrl: 'https://flagcdn.com/us.svg',
  },
  {
    id: BuiltInImportSourceId.MAUDE,
    name: 'MAUDE',
    service: {
      search: () =>
        new MaudeApiClient().search({
          manufacturer: manufacturer.value,
          deviceName: device.value,
          dateFilter: {
            startDate: startDate.value,
            endDate: endDate.value,
          },
        }),
    },
    iconUrl: 'https://flagcdn.com/us.svg',
    isEvidenceMetadataExtractionTechnology: true,
  },
  {
    id: BuiltInImportSourceId.DAEN,
    name: 'DAEN',
    service: {
      search: (query: string) =>
        new DaenApiClient().search({
          query,
          dateFilter: { startDate: startDate.value, endDate: endDate.value },
        }),
    },
    iconUrl: 'https://flagcdn.com/au.svg',
  },
  {
    id: BuiltInImportSourceId.MDID,
    name: 'MDID',
    service: {
      search: (query: string) =>
        new MdidApiClient().search({
          query,
          dateFilter: {
            startDate: startDate.value,
            endDate: endDate.value,
          },
        }),
    },
    iconUrl: 'https://flagcdn.com/ca.svg',
  },
]

const sourcesState = ref<
  Record<
    string,
    {
      loading: boolean
      error: string | null
      items: IncidentMetadata[]
      query: string | null
      filters: string | null
    }
  >
>(
  Object.fromEntries(
    sourcesList.map((s) => [
      s.id,
      { loading: false, error: null, items: [], query: null, filters: null },
    ]),
  ),
)
const device = ref('')
const manufacturer = ref('')
const startDate = ref('')
const endDate = ref('')

const query = computed(() => {
  const deviceValue = device.value.trim()
  const manufacturerValue = manufacturer.value.trim()

  if (deviceValue && manufacturerValue) {
    return `${deviceValue} AND ${manufacturerValue}`
  } else if (deviceValue) {
    return deviceValue
  } else if (manufacturerValue) {
    return manufacturerValue
  }

  return ''
})

const filters = computed(() => {
  const startDateValue = startDate.value.trim()
  const endDateValue = endDate.value.trim()

  if (startDateValue && endDateValue) {
    return `from ${startDateValue} to ${endDateValue}`
  } else if (startDateValue) {
    return `from ${startDateValue}`
  } else if (endDateValue) {
    return `to ${endDateValue}`
  }

  return ''
})

const builtInImportSources = useBuiltInImportSources()

const {
  entity: review,
  addImportSourceToPlan,
  importSearch,
} = injectStrict(ReviewKey)

const snackbar = useSnackbar()
const loading = useLoading()

const isSearching = computed(() =>
  sourcesList.some((s) => sourcesState.value[s.id].loading),
)

const enabledSources = ref(sourcesList.map((s) => s.id))

function getErrorMessage(error: unknown): string {
  // Handle axios errors
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as any
    const status = axiosError.response?.status

    if (status && status >= 500) {
      return 'Database is down'
    }
  }

  return 'Search failed'
}

async function searchSource(
  source: (typeof sourcesList)[number],
  currentQuery: string,
  currentFilters: string,
) {
  sourcesState.value[source.id].loading = true
  sourcesState.value[source.id].error = null
  sourcesState.value[source.id].items = []

  try {
    const result = await source.service.search(currentQuery)
    sourcesState.value[source.id].items = result
    sourcesState.value[source.id].error = null
    sourcesState.value[source.id].query = currentQuery
    sourcesState.value[source.id].filters = currentFilters
  } catch (e) {
    sourcesState.value[source.id].error = getErrorMessage(e)
    sourcesState.value[source.id].items = []
    sourcesState.value[source.id].query = currentQuery
    sourcesState.value[source.id].filters = currentFilters
  } finally {
    sourcesState.value[source.id].loading = false
  }
}

async function toggleSource(sourceId: string) {
  const idx = enabledSources.value.indexOf(sourceId)
  if (idx > -1) {
    enabledSources.value.splice(idx, 1)
  } else {
    enabledSources.value.push(sourceId)

    if (
      query.value &&
      sourcesList.some((s) => sourcesState.value[s.id].query !== null)
    ) {
      const source = sourcesList.find((s) => s.id === sourceId)
      if (source && sourcesState.value[sourceId].query !== query.value) {
        await searchSource(source, query.value, filters.value)
      }
    }
  }
}

async function onSearch() {
  if (!query.value) return

  const searchPromises = sourcesList
    .filter(
      (s) =>
        enabledSources.value.includes(s.id) &&
        sourcesState.value[s.id].query !== query.value,
    )
    .map((s) => searchSource(s, query.value, filters.value))

  await Promise.allSettled(searchPromises)
}
async function retrySource(sourceId: string) {
  if (!enabledSources.value.includes(sourceId)) return
  const source = sourcesList.find((s) => s.id === sourceId)!
  await searchSource(source, query.value, filters.value)
}
const canImport = computed(() => {
  return sourcesList.some(
    (s) =>
      enabledSources.value.includes(s.id) &&
      sourcesState.value[s.id].query !== null,
  )
})
const incidentsCount = computed(() => {
  return sourcesList.reduce(
    (acc, s) =>
      acc +
      (enabledSources.value.includes(s.id)
        ? sourcesState.value[s.id].items.length
        : 0),
    0,
  )
})
function sourceExistsInPlan(importSourceId: string, review: Review): boolean {
  const enabledSources = review.plan?.importPlan.importSources || []
  return enabledSources.some((source) => source.id === importSourceId)
}
async function importIncidents() {
  loading.start()
  try {
    for (const s of sourcesList) {
      if (
        !enabledSources.value.includes(s.id) ||
        sourcesState.value[s.id].error
      )
        continue
      const items = sourcesState.value[s.id].items
      if (!sourceExistsInPlan(s.id, review.value)) {
        const src = builtInImportSources.findById(s.id)
        await addImportSourceToPlan({
          name: src.name,
          type: src.type,
          url: src.url ?? '',
          description: src.description,
        })
      }
      await importSearch({
        query: sourcesState.value[s.id].query ?? '',
        filters: sourcesState.value[s.id].filters ?? '',
        date: new Date().toISOString(),
        importSourceId: s.id,
        items: items.map((a) => ({ ...a, type: ReviewItemType.Incident })),
        url: '',
      })
    }
    snackbar.success('Success')
    reset()
  } catch {
    snackbar.show(SnackbarState.ERROR, 'Failed to import incidents.')
  } finally {
    loading.stop()
  }
}
watch([startDate, endDate], async () => {
  if (query.value) {
    const searchedSources = sourcesList.filter(
      (s) =>
        enabledSources.value.includes(s.id) &&
        sourcesState.value[s.id].query !== null,
    )

    if (searchedSources.length > 0) {
      const searchPromises = searchedSources.map((s) =>
        searchSource(s, query.value, filters.value),
      )
      await Promise.allSettled(searchPromises)
    }
  }
})

function reset() {
  device.value = ''
  manufacturer.value = ''
  startDate.value = ''
  endDate.value = ''
  sourcesList.forEach((s) => {
    sourcesState.value[s.id] = {
      loading: false,
      error: null,
      items: [],
      query: null,
      filters: null,
    }
  })
  enabledSources.value = sourcesList.map((s) => s.id)
}
</script>

<style scoped>
.dot-anim::after {
  content: '';
  display: inline-block;
  width: 1em;
  animation: dots 1.2s steps(3, end) infinite;
}
@keyframes dots {
  0%,
  20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%,
  100% {
    content: '...';
  }
}
</style>
